import React, { FC, KeyboardEvent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Input, SearchGrayAdornment, SearchAdornment } from 'modules/core/components';
import { ColumnWidth, DealSourceProvider } from 'modules/core/enums';
import { getUser } from 'store/selectors/auth.selector';
import { IUser } from 'modules/core/types';
import {
  ROUTE_ONE_SEARCH_FIELD_NAME,
  ROUTE_ONE_DEALER_FIELD_NAME,
  ROUTE_ONE_DEALER_ID_FIELD_NAME,
} from 'modules/Lender/RehashCalculator/RehashCalculator.const';
import { DealerDropdown } from 'modules/Lender/CustomDropdowns/DealerDropdown';

import {
  RouteOneAppTooltip,
  RouteOneErrorTooltip,
  HeaderFieldsContainer,
  FieldCol,
  RouteOneInputWrapper,
  RouteOneDropdownWrapper,
  SearchFieldSpinner,
} from './styles';

interface IDealSourceHeaderFields {
  toShowDealSourceSearchFields: boolean;
  dealSourceFieldHandleChange: (event: any) => void;
  dealSourceDataFlags?: {
    isGetInvalid?: boolean;
    isReceived?: boolean;
  };
  dealSourceConversationId?: string;
  isDealSourceDataEmpty?: boolean;
  isDebouncing?: boolean;
  isVehicleTableLoading?: boolean;
}

export const DealSourceHeaderFields: FC<IDealSourceHeaderFields> = ({
  toShowDealSourceSearchFields,
  dealSourceFieldHandleChange,
  dealSourceDataFlags,
  dealSourceConversationId,
  isDealSourceDataEmpty,
  isDebouncing,
  isVehicleTableLoading,
}) => {
  const { t } = useTranslation();
  const user = useSelector(getUser) as IUser;
  const isWholesaleInventory = user?.withWholesaleInventory || (!!user?.foureyesWebsiteId && !user?.isWithInventory);
  const isDealerTrack = user?.dealSource === DealSourceProvider.DEALER_TRACK;
  const isReadOnly = isWholesaleInventory || isDealerTrack;
  const isDealerTrackConversationIdLoading =
    isDealerTrack && !dealSourceConversationId && isDealSourceDataEmpty && !dealSourceDataFlags?.isGetInvalid;

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        dealSourceFieldHandleChange(event);
      }
    },
    [dealSourceFieldHandleChange],
  );

  if (!toShowDealSourceSearchFields) {
    return null;
  }

  return (
    <HeaderFieldsContainer className="gx-2 align-items-center">
      <FieldCol xs={ColumnWidth.FULL} md={ColumnWidth.XS} className="mb-md-0 mb-2">
        <div className="position-relative">
          <RouteOneInputWrapper $hasError={dealSourceDataFlags?.isGetInvalid && !isDebouncing}>
            <Input
              name={ROUTE_ONE_SEARCH_FIELD_NAME}
              textStyle="bold"
              textPosition="start"
              valueAsNumber={false}
              maxLength={isDealerTrack ? 26 : 21}
              endAdornment={
                isDealerTrackConversationIdLoading ? (
                  <SearchFieldSpinner data-testid="dealer-track-conversation-id-loading" />
                ) : (
                  <SearchGrayAdornment />
                )
              }
              placeholderWithoutAsterisk
              placeholder={t(
                isDealerTrack
                  ? 'components.lender.dealerTrack.searchField.placeholder'
                  : 'components.lender.routeOne.searchField.placeholder',
              )}
              changeCallback={isReadOnly ? undefined : dealSourceFieldHandleChange}
              testId={ROUTE_ONE_SEARCH_FIELD_NAME}
              handleEnterKeyDown={isReadOnly ? undefined : handleKeyDown}
              disabled={isReadOnly || isVehicleTableLoading}
            />
          </RouteOneInputWrapper>
          {!isReadOnly && (
            <RouteOneAppTooltip id={ROUTE_ONE_SEARCH_FIELD_NAME}>
              {t('components.lender.routeOne.searchField.tooltip')}
            </RouteOneAppTooltip>
          )}
          {dealSourceDataFlags?.isGetInvalid && !isDebouncing && (
            <RouteOneErrorTooltip id="route-one-error" isOpen={true} place="bottom">
              {t('components.lender.routeOne.error')}
            </RouteOneErrorTooltip>
          )}
        </div>
      </FieldCol>

      <FieldCol xs={ColumnWidth.FULL} md={ColumnWidth.XS} className="mb-md-0 mb-2">
        {user.isWithInventory ? (
          <RouteOneDropdownWrapper>
            <DealerDropdown
              name={ROUTE_ONE_DEALER_FIELD_NAME}
              changeCallback={dealSourceFieldHandleChange}
              disabled={isReadOnly || isVehicleTableLoading}
            />
          </RouteOneDropdownWrapper>
        ) : (
          <RouteOneInputWrapper>
            <Input
              name={ROUTE_ONE_DEALER_ID_FIELD_NAME}
              valueAsNumber={false}
              endAdornment={<SearchAdornment />}
              maxLength={isDealerTrack ? 10 : 5}
              placeholder={t(
                isDealerTrack
                  ? 'components.lender.dealerTrack.dealerIdField'
                  : 'components.lender.routeOne.dealerIdField',
              )}
              changeCallback={isReadOnly ? undefined : dealSourceFieldHandleChange}
              testId={ROUTE_ONE_DEALER_ID_FIELD_NAME}
              disabled={isReadOnly || isVehicleTableLoading}
            />
          </RouteOneInputWrapper>
        )}
      </FieldCol>
    </HeaderFieldsContainer>
  );
};
